import React from "react";
import {useSelector} from "react-redux";
import {css, useTheme} from "@emotion/react";
import classNames from "classnames";

import {display, mb, mr, mt, onDesktop} from "@pg-design/helpers-css";
import {ClockIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Highlight, Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {dateTimeFormat, formatDate, pluralize} from "@pg-mono/string-utils";

import {IRPStore} from "../../../app/rp_reducer";
import {ArticleRating} from "./ArticleRating";

interface IProps {
    article: {
        author: {
            name: string;
            image: {
                a_img_40x40: string;
            } | null;
        };
        date_published: string;
        date_content_changed: string;
        create_date: string;
        estimated_reading_time: number | null;
        ratings: {
            average: number | null;
            total: number;
        };
        id: number;
        show_reading_time: boolean;
    };
    className?: string;
}

export const ArticleDetailInfoPanel: React.FunctionComponent<IProps> = (props) => {
    const {author, date_published, date_content_changed, ratings, estimated_reading_time, show_reading_time, id: articleID} = props.article;
    const className = classNames(props.className);
    const authorImageSrc = author.image?.a_img_40x40;
    const minutesPlural = pluralize(["minuta", "minuty", "minut"]);
    const textLight = useTheme().colors.gray[600];
    const authorID = useSelector((state: IRPStore) => state.article.detail.article?.author.id);
    const slug = useSelector((state: IRPStore) => state.article.detail.article?.author.slug);
    const link = authorID && slug ? rpAppLink.article.author.detail({authorId: authorID, authorSlug: slug}) : undefined;
    const isFeatured = useSelector((state: IRPStore) => state.article.detail.article?.author.is_featured);

    return (
        <div css={articleDetailInfoPanel} className={className}>
            <div css={leftPanel}>
                {authorImageSrc && <Image css={authorIcon} src={authorImageSrc} alt={author.name} height="40px" width="40px" />}
                <div>
                    <Text css={[display("inline-block"), mb(1)]} variant="button_micro">
                        <Highlight as={isFeatured ? "a" : "span"} href={isFeatured ? link : undefined}>
                            {author.name}
                        </Highlight>
                    </Text>

                    <Text color={textLight} as="p" variant="info_txt_1">
                        Data publikacji: {formatDate(date_published, dateTimeFormat.shortDate)}
                        {date_content_changed && ", "}
                        {date_content_changed && (
                            <span css={[display("block"), onDesktop(display("inline"))]}>
                                Data aktualizacji: {formatDate(date_content_changed, dateTimeFormat.shortDate)}
                            </span>
                        )}
                    </Text>

                    {show_reading_time && !!estimated_reading_time && (
                        <Text as="div" variant="info_txt_1" color={textLight} css={timeInfoText}>
                            <ClockIcon fill={textLight} size="1.8" css={mr(1)} />
                            <span>
                                {estimated_reading_time} {minutesPlural(estimated_reading_time)} czytania
                            </span>
                        </Text>
                    )}
                </div>
            </div>
            <ArticleRating articleId={articleID} ratings={ratings} />
        </div>
    );
};

const timeInfoText = css`
    ${mt(2)};
    vertical-align: baseline;
`;
const articleDetailInfoPanel = css`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
    flex-basis: 100%;
`;

const leftPanel = css`
    align-items: flex-start;
    display: flex;
    min-width: 60%;
`;

const authorIcon = css`
    height: 40px;
    width: 40px;
    min-width: 40px;
    border-radius: 50%;
    ${mr(3)}
`;
